import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Menu, X, Moon, Sun, Monitor, Languages, Palette, Play, Sparkles,
  LayoutDashboard, LogIn, LogOut, User as UserIcon, UserPlus,
} from "lucide-react";
import {
  useTheme, ACCENT_COLORS, LANGUAGE_OPTIONS, type ThemeMode,
} from "@/lib/theme-context";
import { useAuth } from "@/lib/auth";
import { useUserAuth } from "@/lib/user-auth";
import { useAllItems } from "@/lib/library-store";
import { smartSearch } from "@/lib/search";
import { OfflineBar } from "@/components/rawan/OfflineBar";
import { UpcomingEpisodeWidget } from "@/components/rawan/UpcomingEpisodeWidget";
import { pickTitle } from "@/lib/i18n";
import { NotifyBell } from "@/components/rawan/NotifyBell";

type PopoverKey = null | "theme" | "language" | "menu" | "search" | "profile";

const SEARCH_DEBOUNCE_MS = 180;

/**
 * Neutralise ad-network popunder triggers around a brand click.
 *
 * Popunder scripts (Adsterra & co.) attach `document`-level click
 * listeners that call `window.open(...)`. On a logo click users expect
 * a plain navigation — never a new window/tab. We defend in two ways:
 *   1. `stopPropagation` in the capture phase so React handlers on
 *      ancestors don't fire.
 *   2. Temporarily replace `window.open` with a no-op for ~800ms so
 *      any document-level listener that still runs cannot open a tab.
 */
function guardBrandClick(e: { stopPropagation: () => void }) {
  try {
    e.stopPropagation();
  } catch {
    /* ignore */
  }
  if (typeof window === "undefined") return;
  const w = window as unknown as { open: typeof window.open; __rawanBrandGuard?: number };
  if (w.__rawanBrandGuard) return;
  const original = w.open;
  w.open = () => null;
  w.__rawanBrandGuard = window.setTimeout(() => {
    w.open = original;
    w.__rawanBrandGuard = undefined;
  }, 800);
}

export function Header() {
  const {
    dir, language, setLanguage, isDark, mode, setMode,
    accent, accentIndex, setAccent, autoCycle, toggleAutoCycle,
  } = useTheme();
  const { isAuthed, logout } = useAuth();
  const { user, signOut } = useUserAuth();
  const [active, setActive] = useState<PopoverKey>(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [highlight, setHighlight] = useState(0);
  const headerRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);

  /* ---------- outside click / escape ---------- */
  useEffect(() => {
    if (!active) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target as Node)) setActive(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setActive(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [active]);

  /* ---------- search debounce ---------- */
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const toggle = (k: Exclude<PopoverKey, null>) => setActive((p) => (p === k ? null : k));

  const links = [
    { to: "/",         ar: "الرئيسية", en: "Home",    fr: "Accueil" },
    { to: "/movies",   ar: "أفلام",    en: "Movies",  fr: "Films" },
    { to: "/series",   ar: "مسلسلات",  en: "Series",  fr: "Séries" },
    { to: "/contact",  ar: "اتصل",     en: "Contact", fr: "Contact" },
    { to: "/sitemap",  ar: "الفهرس",   en: "Sitemap", fr: "Plan" },
  ] as const;

  const modeOptions: { value: ThemeMode; icon: typeof Sun; ar: string; en: string; fr: string }[] = [
    { value: "light",  icon: Sun,     ar: "فاتح",   en: "Light",  fr: "Clair" },
    { value: "dark",   icon: Moon,    ar: "غامق",   en: "Dark",   fr: "Sombre" },
    { value: "system", icon: Monitor, ar: "النظام", en: "System", fr: "Système" },
  ];

  /* ---------- Smart, fuzzy, Arabic-aware suggestions ---------- */
  const allItems = useAllItems();
  const suggestions = useMemo(() => {
    if (!debounced) return [] as typeof allItems;
    return smartSearch(allItems, debounced, 8).map((r) => r.item);
  }, [debounced, allItems]);

  useEffect(() => { setHighlight(0); }, [debounced]);

  const goToItem = (m: (typeof allItems)[number]) => {
    navigate({
      to: m.type === "series" ? "/series/$slug" : "/movie/$slug",
      params: { slug: m.slug },
    });
    setActive(null); setQuery(""); setDebounced("");
  };

  const submitSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!debounced) return;
    const pick = suggestions[highlight] ?? suggestions[0];
    if (pick) goToItem(pick);
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (active !== "search" || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToItem(suggestions[highlight]);
    }
  };

  const panelOffset = "top-[calc(100%+0.75rem)]";
  const solidSurface = "bg-background border border-border shadow-2xl";
  const iconBtnClass =
    "grid place-items-center w-9 h-9 rounded-full bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground border border-border/70 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background relative";
  const optionBtnBase =
    "bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground border border-border/60";

  /* IMPORTANT: header icons are visible ONLY at xl (where the hamburger is hidden).
     Between md and lg (tablet) the hamburger menu is shown, so all controls live
     inside that menu — never duplicated in the header. */
  const showOnDesktop = "hidden xl:inline-flex";
  const hideOnDesktop = "xl:hidden";

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 glass-strong border-b border-border"
    >
      <div className="container mx-auto grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:gap-3 px-2.5 sm:px-4 h-14 sm:h-16">
        {/* Brand — popup-guarded: some ad networks hook document-level
            click listeners to open popunders on ANY click. We swallow
            those triggers around brand clicks by (a) stopping propagation
            in the capture phase and (b) temporarily neutralising
            window.open for ~800ms after the interaction. */}
        <Link
          to="/"
          className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0"
          onPointerDownCapture={guardBrandClick}
          onMouseDownCapture={guardBrandClick}
          onTouchStartCapture={guardBrandClick}
          onClickCapture={(e) => e.stopPropagation()}
          onAuxClick={(e) => e.preventDefault()}
        >
          <div
            className="relative grid place-items-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl shrink-0"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Play className="h-4 w-4 text-primary-foreground fill-current" />
            <Sparkles
              className="absolute -top-1 -right-1 h-3 w-3 text-black fill-black drop-shadow-[0_0_2px_rgba(0,0,0,0.35)] dark:text-white dark:fill-white dark:drop-shadow-none"
            />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-extrabold text-base sm:text-lg tracking-tight truncate">
              Rawan<span className="text-gradient">UI</span>
            </div>
            <div className="block text-[10px] text-muted-foreground -mt-0.5 truncate">
              {t("\u0623\u0641\u0644\u0627\u0645 \u0648 \u0645\u0633\u0644\u0633\u0644\u0627\u062a", "Movies & Series", "Films & S\u00e9ries")}
            </div>
          </div>
        </Link>

        {/* Middle: nav (xl) + search (always) */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <nav className="hidden xl:flex items-center gap-0.5 shrink-0">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-2.5 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors whitespace-nowrap"
                activeProps={{ className: "text-foreground bg-accent" }}
              >
                {t(l.ar, l.en, l.fr)}
              </Link>
            ))}
          </nav>

          <form
            onSubmit={submitSearch}
            className="relative flex-1 min-w-0 max-w-md mx-auto md:mx-0"
            role="search"
          >
            <Search className="absolute top-1/2 -translate-y-1/2 start-2.5 sm:start-3 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(e.target.value ? "search" : null); }}
              onFocus={() => { if (query) setActive("search"); }}
              onKeyDown={onSearchKey}
              placeholder={t("\u0627\u0628\u062d\u062b\u2026", "Search\u2026", "Rechercher\u2026")}
              aria-label={t("\u0628\u062d\u062b", "Search", "Recherche")}
              aria-autocomplete="list"
              aria-controls={active === "search" && suggestions.length > 0 ? "rawan-search-listbox" : undefined}
              aria-activedescendant={
                active === "search" && suggestions.length > 0 ? `rawan-search-opt-${highlight}` : undefined
              }
              autoComplete="off"
              className="w-full ps-8 sm:ps-9 pe-3 py-2 text-sm bg-card/60 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
            {active === "search" && suggestions.length > 0 && (
              <div className={`absolute start-0 end-0 ${panelOffset} ${solidSurface} rounded-xl overflow-hidden z-50 animate-fade-in`}>
                <ul id="rawan-search-listbox" className="max-h-80 overflow-y-auto py-1" role="listbox">
                  {suggestions.map((m, i) => (
                    <li key={m.id}>
                      <Link
                        id={`rawan-search-opt-${i}`}
                        to={m.type === "series" ? "/series/$slug" : "/movie/$slug"}
                        params={{ slug: m.slug }}
                        role="option"
                        aria-selected={i === highlight}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => { setActive(null); setQuery(""); setDebounced(""); }}
                        className={`flex items-center gap-3 px-3 py-2 transition-colors ${
                          i === highlight ? "bg-accent" : "hover:bg-accent"
                        }`}
                      >
                        <img src={m.poster} alt="" className="w-8 h-12 object-cover rounded shrink-0" loading="lazy" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{pickTitle(m, language)}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {m.year} • {m.type === "series" ? t("\u0645\u0633\u0644\u0633\u0644", "Series", "S\u00e9rie") : t("\u0641\u064a\u0644\u0645", "Movie", "Film")}
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {active === "search" && debounced && suggestions.length === 0 && (
              <div className={`absolute start-0 end-0 ${panelOffset} ${solidSurface} rounded-xl p-4 z-50 text-center`}>
                <p className="text-sm font-semibold">
                  {t(`لا نتائج لـ “${query}”`, `No results for “${query}”`, `Aucun résultat pour « ${query} »`)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("جرّب البحث بكلمات أخرى أو تصفّح من القوائم.", "Try a different keyword or browse the lists.", "Essayez un autre mot-clé ou parcourez les listes.")}
                </p>
                <div className="flex justify-center gap-2 mt-3">
                  <Link
                    to="/movies"
                    onClick={() => { setActive(null); setQuery(""); }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground"
                  >
                    {t("تصفّح الأفلام", "Browse movies", "Parcourir les films")}
                  </Link>
                  <Link
                    to="/series"
                    onClick={() => { setActive(null); setQuery(""); }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-card border border-border hover:bg-accent"
                  >
                    {t("تصفّح المسلسلات", "Browse series", "Parcourir les séries")}
                  </Link>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* End controls — bell is always visible, the rest only at xl+ (hamburger handles mobile/tablet) */}
        <div className="flex items-center gap-0.5 sm:gap-1 xl:gap-1.5 shrink-0" data-testid="header-end-controls">
          <NotifyBell />
          {/* Theme */}

          <div className={`relative ${showOnDesktop}`}>
            <button
              onClick={() => toggle("theme")}
              className={iconBtnClass}
              aria-label={t("\u0627\u0644\u0633\u0645\u0629 \u0648\u0627\u0644\u0648\u0636\u0639", "Theme & mode", "Th\u00e8me & mode")}
              title={t("\u0627\u0644\u0633\u0645\u0629 \u0648\u0627\u0644\u0648\u0636\u0639", "Theme & mode", "Th\u00e8me & mode")}
            >
              <Palette className="h-4 w-4" />
              <span
                className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-background"
                style={{ background: accent.hex }}
              />
            </button>
            {active === "theme" && (
              <ThemePanel
                t={t} isAr={isAr} accentIndex={accentIndex} setAccent={setAccent}
                autoCycle={autoCycle} toggleAutoCycle={toggleAutoCycle}
                mode={mode} setMode={setMode} modeOptions={modeOptions}
                onClose={() => setActive(null)} offsetClass={panelOffset} surfaceClass={solidSurface}
              />
            )}
          </div>

          {/* Language */}
          <div className={`relative ${showOnDesktop}`}>
            <button
              onClick={() => toggle("language")}
              className={iconBtnClass}
              aria-label={`${language.toUpperCase()} — ${t("\u0627\u0644\u0644\u063a\u0629", "Language", "Langue")}`}
              title={t("\u0627\u0644\u0644\u063a\u0629", "Language", "Langue")}
            >
              <Languages className="h-4 w-4" />
              <span className="absolute -bottom-1 -end-1 rounded-md bg-primary px-1 text-[9px] font-black leading-4 text-primary-foreground">
                {language.toUpperCase()}
              </span>
            </button>
            {active === "language" && (
              <div className={`absolute end-0 ${panelOffset} w-60 ${solidSurface} rounded-2xl p-2 animate-scale-in z-50`}>
                <div className="grid grid-cols-3 gap-1.5">
                  {LANGUAGE_OPTIONS.map((option) => {
                    const sel = language === option.code;
                    return (
                      <button
                        key={option.code}
                        onClick={() => { setLanguage(option.code); setActive(null); }}
                        className={`flex flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-2 text-xs transition ${
                          sel ? "bg-primary text-primary-foreground" : optionBtnBase
                        }`}
                      >
                        <span className="text-[11px] font-black uppercase">{option.code}</span>
                        <span className="truncate text-[11px] w-full text-center">{option.native}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Unified Profile button (desktop only). Popover content depends on auth state. */}
          <div className={`relative ${showOnDesktop}`}>
            <button
              onClick={() => toggle("profile")}
              className={iconBtnClass}
              aria-label={t("\u0627\u0644\u062d\u0633\u0627\u0628", "Account", "Compte")}
              aria-haspopup="menu"
              aria-expanded={active === "profile"}
              title={t("\u0627\u0644\u062d\u0633\u0627\u0628", "Account", "Compte")}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <UserIcon className="h-4 w-4" />
              )}
              {(isAuthed || user) && (
                <span
                  className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-background"
                  aria-hidden
                />
              )}
            </button>
            {active === "profile" && (
              <div
                role="menu"
                className={`absolute end-0 ${panelOffset} w-60 ${solidSurface} rounded-2xl p-2 z-50 animate-scale-in`}
              >
                {isAuthed ? (
                  <>
                    <div className="px-3 py-2 text-xs text-muted-foreground uppercase tracking-wider">
                      {t("\u0644\u0648\u062d\u0629 \u0627\u0644\u0645\u062f\u064a\u0631", "Admin", "Admin")}
                    </div>
                    <Link
                      to="/dashboard"
                      role="menuitem"
                      onClick={() => setActive(null)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent text-sm font-semibold"
                    >
                      <LayoutDashboard className="h-4 w-4" /> {t("\u0644\u0648\u062d\u0629 \u0627\u0644\u062a\u062d\u0643\u0645", "Dashboard", "Tableau de bord")}
                    </Link>
                    <button
                      role="menuitem"
                      onClick={() => { logout(); setActive(null); }}
                      className="w-full text-start flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent text-sm font-semibold text-destructive"
                    >
                      <LogOut className="h-4 w-4" /> {t("\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062e\u0631\u0648\u062c", "Logout", "D\u00e9connexion")}
                    </button>
                  </>
                ) : user ? (
                  <>
                    <div className="px-3 py-2">
                      <div className="text-sm font-bold truncate">{user.displayName}</div>
                      {user.email && (
                        <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
                      )}
                    </div>
                    <div className="border-t border-border/60 my-1" />
                    <Link
                      to="/account"
                      role="menuitem"
                      onClick={() => setActive(null)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent text-sm font-semibold"
                    >
                      <UserIcon className="h-4 w-4" /> {t("\u062d\u0633\u0627\u0628\u064a", "My account", "Mon compte")}
                    </Link>
                    <button
                      role="menuitem"
                      onClick={() => { signOut(); setActive(null); }}
                      className="w-full text-start flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent text-sm font-semibold text-destructive"
                    >
                      <LogOut className="h-4 w-4" /> {t("\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062e\u0631\u0648\u062c", "Logout", "D\u00e9connexion")}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground uppercase tracking-wider">
                      {t("\u0645\u0631\u062d\u0628\u0627\u064b \u0628\u0643", "Welcome", "Bienvenue")}
                    </div>
                    <Link
                      to="/login"
                      role="menuitem"
                      onClick={() => setActive(null)}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background text-sm font-bold hover:opacity-90"
                    >
                      <LogIn className="h-4 w-4" /> {t("\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644", "Login", "Connexion")}
                    </Link>
                    <Link
                      to="/signup"
                      role="menuitem"
                      onClick={() => setActive(null)}
                      className="mt-1.5 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90"
                    >
                      <UserPlus className="h-4 w-4" /> {t("\u0625\u0646\u0634\u0627\u0621 \u062d\u0633\u0627\u0628", "Sign up", "S'inscrire")}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Hamburger — visible until xl. Holds EVERYTHING (nav + theme + lang + auth). */}
          <button
            onClick={() => toggle("menu")}
            className={`${hideOnDesktop} p-2 rounded-lg hover:bg-accent`}
            aria-label={t("\u0627\u0644\u0642\u0627\u0626\u0645\u0629", "Menu", "Menu")}
            aria-expanded={active === "menu"}
          >
            {active === "menu" ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Connectivity status bar — sticky with the header on every breakpoint */}
      <OfflineBar />

      {/* Mobile/tablet menu panel (also shown on tablets so the header stays minimal) */}
      {active === "menu" && (
        <div className={`${hideOnDesktop} absolute inset-x-0 ${panelOffset} mx-3 ${solidSurface} rounded-2xl animate-fade-in z-50 max-h-[calc(100vh-5rem)] overflow-y-auto`}>
          <nav className="px-3 py-3 flex flex-col gap-1">
            {/* Top row: nav links on one side, upcoming-episode widget on the opposite side.
                Widget is mobile/tablet-only; desktop never sees this panel. */}
            <div className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-2 items-stretch">
              <div className="flex flex-col gap-1 min-w-0 h-full">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setActive(null)}
                    className="px-2 py-2 rounded-lg text-[12px] font-semibold text-center bg-secondary/50 text-secondary-foreground hover:bg-accent hover:text-accent-foreground border border-border/50 transition-colors truncate"
                    activeProps={{ className: "bg-primary/15 text-primary border-primary/40" }}
                  >
                    {t(l.ar, l.en, l.fr)}
                  </Link>
                ))}
              </div>
              <div className="min-w-0 h-full">
                <UpcomingEpisodeWidget compact />
              </div>
            </div>

            <div className="my-2 border-t border-border/60" />

            <details className="px-3 py-1 group">
              <summary className="flex items-center justify-between cursor-pointer list-none py-2 rounded-lg hover:bg-accent px-1">
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Palette className="h-4 w-4" />
                  {t("\u0627\u0644\u0644\u0648\u0646 \u0627\u0644\u0645\u0645\u064a\u0632", "Accent", "Couleur d'accent")}
                </span>
                <span className="w-3.5 h-3.5 rounded-full ring-2 ring-background" style={{ background: accent.hex }} />
              </summary>
              <div className="mt-2 px-1 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("\u0627\u062e\u062a\u0631 \u0627\u0644\u0644\u0648\u0646", "Pick a color", "Choisir une couleur")}
                  </span>
                  <button
                    onClick={toggleAutoCycle}
                    className={`text-[10px] px-2 py-1 rounded-md font-bold transition ${
                      autoCycle ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {autoCycle ? t("\u062a\u0644\u0642\u0627\u0626\u064a", "AUTO", "AUTO") : t("\u064a\u062f\u0648\u064a", "MANUAL", "MANUEL")}
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {ACCENT_COLORS.map((c, i) => (
                    <button
                      key={c.name}
                      onClick={() => setAccent(i)}
                      className={`color-dot ${i === accentIndex ? "active" : ""}`}
                      style={{ background: c.hex }}
                      aria-label={c.name}
                      title={isAr ? c.nameAr : c.name}
                    />
                  ))}
                </div>
              </div>
            </details>

            <details className="px-3 py-1">
              <summary className="flex items-center justify-between cursor-pointer list-none py-2 rounded-lg hover:bg-accent px-1">
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  {mode === "system" ? <Monitor className="h-4 w-4" /> : isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {t("الوضع", "Mode", "Mode")}
                </span>
                <span className="text-[10px] uppercase font-bold text-muted-foreground">
                  {mode === "light" ? t("\u0641\u0627\u062a\u062d", "Light", "Clair") : mode === "dark" ? t("\u063a\u0627\u0645\u0642", "Dark", "Sombre") : t("نظام", "System", "Système")}
                </span>
              </summary>
              <div className="mt-2 px-1 pb-2 grid grid-cols-3 gap-1">
                {modeOptions.map((opt) => {
                  const Icon = opt.icon;
                  const sel = mode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setMode(opt.value)}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition ${
                        sel ? "bg-primary text-primary-foreground" : optionBtnBase
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{t(opt.ar, opt.en, opt.fr)}</span>
                    </button>
                  );
                })}
              </div>
            </details>

            <div className="px-3 py-2">
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {t("\u0627\u0644\u0644\u063a\u0629", "Language", "Langue")}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {LANGUAGE_OPTIONS.map((option) => {
                  const sel = language === option.code;
                  return (
                    <button
                      key={option.code}
                      onClick={() => setLanguage(option.code)}
                      className={`flex flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-2 text-xs transition ${
                        sel ? "bg-primary text-primary-foreground" : optionBtnBase
                      }`}
                    >
                      <span className="text-[11px] font-black uppercase">{option.code}</span>
                      <span className="truncate text-[11px] w-full text-center">{option.native}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="my-2 border-t border-border/60" />

            {/* Auth — full set in menu */}
            {isAuthed ? (
              <div className="grid grid-cols-2 gap-2 px-1">
                <Link
                  to="/dashboard"
                  onClick={() => setActive(null)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-secondary text-secondary-foreground hover:bg-accent inline-flex items-center justify-center gap-2 border border-border/60"
                >
                  <LayoutDashboard className="h-4 w-4" /> {t("\u0644\u0648\u062d\u0629 \u0627\u0644\u062a\u062d\u0643\u0645", "Dashboard", "Tableau de bord")}
                </Link>
                <button
                  onClick={() => { logout(); setActive(null); }}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-destructive text-destructive-foreground hover:brightness-110 inline-flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" /> {t("خروج", "Logout", "Déconnexion")}
                </button>
              </div>
            ) : user ? (
              <div className="grid grid-cols-2 gap-2 px-1">
                <Link
                  to="/account"
                  onClick={() => setActive(null)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-secondary text-secondary-foreground hover:bg-accent inline-flex items-center justify-center gap-2 border border-border/60"
                >
                  <UserIcon className="h-4 w-4" /> {t("\u062d\u0633\u0627\u0628\u064a", "Account", "Mon compte")}
                </Link>
                <button
                  onClick={() => { signOut(); setActive(null); }}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-destructive text-destructive-foreground hover:brightness-110 inline-flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" /> {t("خروج", "Logout", "Déconnexion")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 px-1">
                <Link
                  to="/login"
                  onClick={() => setActive(null)}
                  className="px-3 py-2 rounded-full text-sm font-semibold bg-foreground text-background inline-flex items-center justify-center gap-2"
                >
                  <LogIn className="h-4 w-4" /> {t("دخول", "Login", "Connexion")}
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setActive(null)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2"
                >
                  <UserPlus className="h-4 w-4" /> {t("\u0625\u0646\u0634\u0627\u0621 \u062d\u0633\u0627\u0628", "Sign up", "S'inscrire")}
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

/* ---------- Combined Theme popover (desktop) ---------- */
function ThemePanel({
  t, isAr, accentIndex, setAccent, autoCycle, toggleAutoCycle,
  mode, setMode, modeOptions, onClose, offsetClass, surfaceClass,
}: {
  t: (ar: string, en: string, fr?: string) => string;
  isAr: boolean;
  accentIndex: number;
  setAccent: (i: number) => void;
  autoCycle: boolean;
  toggleAutoCycle: () => void;
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  modeOptions: { value: ThemeMode; icon: typeof Sun; ar: string; en: string; fr: string }[];
  onClose: () => void;
  offsetClass: string;
  surfaceClass: string;
}) {
  return (
    <div className={`absolute end-0 ${offsetClass} w-72 ${surfaceClass} rounded-2xl p-3 animate-scale-in z-50`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("\u0627\u0644\u0644\u0648\u0646 \u0627\u0644\u0645\u0645\u064a\u0632", "Accent", "Couleur d'accent")}
        </span>
        <button
          onClick={toggleAutoCycle}
          className={`text-[10px] px-2 py-1 rounded-md font-bold transition ${
            autoCycle ? "bg-primary text-primary-foreground animate-pulse-glow" : "bg-muted text-muted-foreground hover:bg-accent"
          }`}
        >
          {autoCycle ? t("تلقائي ●", "AUTO ●") : t("\u064a\u062f\u0648\u064a", "MANUAL", "MANUEL")}
        </button>
      </div>
      <div className="grid grid-cols-7 gap-2 p-1">
        {ACCENT_COLORS.map((c, i) => (
          <button
            key={c.name}
            onClick={() => setAccent(i)}
            className={`color-dot ${i === accentIndex ? "active" : ""}`}
            style={{ background: c.hex }}
            aria-label={c.name}
            title={isAr ? c.nameAr : c.name}
          />
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-border/60">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          {t("الوضع", "Mode", "Mode")}
        </div>
        <div className="grid grid-cols-3 gap-1">
          {modeOptions.map((opt) => {
            const Icon = opt.icon;
            const sel = mode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => { setMode(opt.value); onClose(); }}
                className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition ${
                  sel ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground border border-border/60"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{t(opt.ar, opt.en, opt.fr)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
